import { Injectable } from '@angular/core';
// import * as RecordRTC from 'recordrtc';
import { MicVAD } from '@ricky0123/vad-web';
import { SpeechProbabilities } from '@ricky0123/vad-web/dist/models';
import * as RecordRTC from 'recordrtc';
import { Subject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class AudioCaptureService {
  private stream: MediaStream;
  private audioContext: AudioContext;
  private recordRTC: RecordRTC;
  private vad: MicVAD;
  private silenceTimeout: any;
  // Duration (ms) to wait after silence is detected before stopping the recording.
  private silenceDuration = 150;
  private isRecording = false;

  // Subject to emit the blob when recording is stopped automatically (on silence)
  public recordingCompleted$ = new Subject<Blob>();
  public recordingStarted$ = new Subject<void>();
  
  constructor() {}

  /**
   * Initialize microphone access, AudioContext, MicVAD, and RecordRTC.
   * Follows the official documentation from:
   * https://docs.vad.ricky0123.com/user-guide/browser/
   */
  async init(): Promise<void> {
    // Request microphone access.
    this.stream = await navigator.mediaDevices.getUserMedia({ audio: true });

    // Set up the AudioContext and create a source node from the stream.
    this.audioContext = new AudioContext();
    const source = this.audioContext.createMediaStreamSource(this.stream);


    this.vad = await MicVAD.new({
      redemptionFrames: 4,
      positiveSpeechThreshold: 0.55,
      negativeSpeechThreshold : 0.4,
      preSpeechPadFrames: 2,
      onSpeechStart: this.onVoiceStart.bind(this),
      onSpeechEnd: this.onVoiceStop.bind(this),
      onFrameProcessed: (probabilities: SpeechProbabilities, frame: Float32Array) => {
        // console.log('Voice update:', probabilities);
      }
    });

    this.vad.start(); // Begin monitoring voice activity.

    // Initialize RecordRTC for audio recording.
    this.recordRTC = new RecordRTC(this.stream, {
      type: 'audio',
      mimeType: 'audio/webm'
    });
  }

  /**
   * Starts recording audio using RecordRTC.
   */
  startRecording(): void {
    if (this.recordRTC) {
      this.isRecording = true;
      this.recordRTC.startRecording();
      this.recordingStarted$.next();
      console.log('Recording started.');
    } else {
      console.error('Recorder not initialized. Call init() first.');
    }
  }

  /**
   * Stops the recording and returns a Promise that resolves with the audio blob.
   */
  endRecording(): Promise<Blob> {
    return new Promise((resolve, reject) => {
      if (!this.recordRTC) {
        return reject('Recorder not initialized.');
      }
      this.isRecording = false;
      this.recordRTC.stopRecording(() => {
        const blob = this.recordRTC.getBlob();
        resolve(blob);
      });
    });
  }
 
  /**
   * Callback triggered by MicVAD when voice is detected.
   * Clears any pending silence timeout.
   */
  onVoiceStart(): void {
    console.log('Voice detected.');
    // Cancel any pending silence timeout.
    if (this.silenceTimeout) {
      clearTimeout(this.silenceTimeout);
      this.silenceTimeout = null;
    }
    // Automatically start recording if not already recording.
    if (!this.isRecording) {
      this.startRecording();
    }
  }

  /**
   * Callback triggered by MicVAD when voice stops.
   * After a defined period of silence, stops the recording.
   */
  onVoiceStop(): void {
    if (!this.isRecording) {
      return;
    }
    
    console.log('Silence detected.');
    if (!this.silenceTimeout) {
      this.silenceTimeout = setTimeout(() => {
        this.endRecording().then((blob) => {
          console.log('Recording stopped due to silence.', blob);
          // Emit the blob to the subject.
          this.recordingCompleted$.next(blob);
        });
      }, this.silenceDuration);
    }
  }

  stopCapturing(): void {
    // Stop any ongoing recording.
    if (this.isRecording) {
      this.endRecording().catch(err => console.error('Error stopping recording:', err));
    }
    // Stop the VAD if it's running.
    if (this.vad) {
      this.vad.destroy();
    }
    // Stop all tracks on the media stream to release the microphone.
    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop());
    }
    // Reset the audio context if needed.
    if (this.audioContext && this.audioContext.state !== 'closed') {
      this.audioContext.close().catch(err => console.error('Error closing audio context:', err));
    }
    console.log('Audio capturing has been stopped.');
  }  
}
